// src/services/PresenceService.js
//
// Who is currently in a building, from the door system.
//
// Two things about a person's location are not the same:
//
//   Entra officeLocation  - where they nominally sit
//   the door they used    - where they actually are
//
// Somebody based at Birkerod who badges into Hillerod is at Hillerod, and the
// screens should say so. This returns the second.
//
// Matched to the directory by Entra object id. The link between a Unifi badge
// and a Microsoft account is recorded under Admin > Badges; nothing else
// connects the two systems.

import apiClient from './apiClient'

export async function getCurrentPresence() {
  const response = await apiClient.post('/GetCurrentPresence', {})

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read presence')
  }

  // Keyed by Entra object id, which is what the badge mapping resolves to.
  //
  // This used to key on email, because email was the only value the door side
  // and the directory had in common. Now that the mapping is explicit the
  // object id can be used, which does not change when somebody marries.
  //
  // People who are not inside are simply absent, so a lookup miss means "not
  // checked in" without needing a second state.
  const map = {}
  for (const row of response.data.present ?? []) {
    if (row.entra_object_id) map[row.entra_object_id] = row
  }
  return map
}

/** Two-letter badge for an office name, matching the display convention. */
export function officeBadge(office) {
  if (!office) return '\u2013'
  return office.trim().slice(0, 2).toUpperCase()
}

/**
 * Was this person here at any point today, in the country's own day?
 *
 * A badge lasts the day it was earned. Somebody who came in at nine and left at
 * five is still shown in the evening, greyed, because they were here today.
 * Tomorrow morning they are not, until they badge again.
 *
 * The comparison has to happen in the LOCATION's timezone, not the viewer's and
 * not UTC. A Copenhagen check-in at 00:30 is 22:30 the previous day in UTC, and
 * judging it by UTC would drop it from the board the moment it happened.
 */

/**
 * The backend sends event times in UTC but without a trailing "Z"
 * ("2026-10-15T23:26:47"). JavaScript reads an ISO date-time with no offset as
 * the BROWSER's local time, so a 23:26 UTC tap became 23:26 Copenhagen, which
 * is the previous day once the clock passes midnight there. The board then
 * dropped the person as "not today" for the first two hours of each day, and
 * every evening on the Texas screen.
 *
 * Marking it UTC when no offset is present fixes the parse; a value that
 * already carries an offset is left alone.
 */
export function parseUtc(iso) {
  if (typeof iso !== 'string') return new Date(NaN)
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z')
}

export function seenToday(row, timeZone) {
  if (!row?.since) return false

  const asDate = (d) =>
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      ...(timeZone ? { timeZone } : {})
    }).format(d)

  // Invalid dates format as something that will never match, rather than
  // throwing and taking the whole board with them.
  const event = parseUtc(row.since)
  if (Number.isNaN(event.getTime())) return false

  return asDate(event) === asDate(new Date())
}

/** Currently inside: here today, and the last thing they did was come in. */
export function isCheckedIn(row, timeZone) {
  return seenToday(row, timeZone) && row.direction === 'ENTERED'
}

/**
 * The timezone a country's day is measured in.
 *
 * Whether an event counts as "today" has to be judged where the door is, not
 * where the viewer is: a Copenhagen tap at 00:30 is 22:30 the previous day in
 * UTC, and a colleague looking from Karachi should still see it as today's.
 *
 * Unlisted countries fall back to the viewer's timezone, which is wrong in
 * principle and close enough for a country nobody has configured.
 */
const COUNTRY_ZONES = {
  DK: 'Europe/Copenhagen',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DE: 'Europe/Berlin',
  NL: 'Europe/Amsterdam',
  PL: 'Europe/Warsaw',
  GB: 'Europe/London',
  US: 'America/Chicago',   // Texas
  SG: 'Asia/Singapore',
  PK: 'Asia/Karachi',
  IN: 'Asia/Kolkata',
  VN: 'Asia/Ho_Chi_Minh'
}

export function zoneFor(countryCode) {
  return COUNTRY_ZONES[countryCode]
}
